'use client';

import { useState } from 'react';
import { z } from 'zod';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldError(null);

    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Invalid email');
      return;
    }

    setBusy(true);
    try {
      // We intentionally don't branch on the response status. Better Auth
      // returns 200 whether or not the email exists (enumeration-safe).
      // On a server error we still show the generic success message — better
      // to let the user retry than leak infrastructure state.
      await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: parsed.data.email,
          redirectTo: '/reset-password',
        }),
      });
    } catch {
      // swallow — see comment above
    } finally {
      setBusy(false);
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3">
        If that email exists, we&apos;ve sent a reset link. Check your inbox
        (and spam folder). The link expires in 1 hour.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="email" className="block text-xs font-semibold text-[#0f2534] mb-1">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-slate-200 focus:border-[#ff9100] focus:ring-1 focus:ring-[#ff9100] outline-none px-3 py-2 text-sm"
        />
        {fieldError && <p className="mt-1 text-xs text-red-600">{fieldError}</p>}
      </div>

      <button
        type="submit"
        disabled={busy}
        className="w-full bg-[#ff9100] hover:bg-[#ff7f00] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
      >
        {busy ? 'Sending…' : 'Send reset link'}
      </button>
    </form>
  );
}
